import { Input, message } from 'antd'
import { MyCheckbox } from '~/components/ui/MyCheckbox'
import { MyForm } from '~/components/ui/MyForm'
import { crossLogin, graphQLErrorMessage, AreaCodeEnum } from '~/utils'
import { ValidInfoProps, Captcha } from './Captcha'
import { MyBtn } from '~/components/ui/MyBtn'
import { useState, useCallback, useMemo } from 'react'
import {
  getMobileCode,
  getMobileCodeVariables,
  loginByMobile,
  loginByMobileVariables,
} from '~/graphqls/types'
import { GET_MOBILE_CODE, LOGIN_BY_MOBILE } from '~/graphqls/gqls'
import { useMutation } from '@apollo/client'
import Cookies from 'js-cookie'
import { MyLink } from '~/components/ui/MyLink'
import { useStores } from '~/hooks/useStores'
import { observer } from 'mobx-react-lite'
import { useDeviceInfo } from '~/hooks/useDeviceInfo'
import AreaCodeSelector, { handleMobileReg } from './AreaCodeSelector'

export const CodeLogin: React.FC<{
  setVisible?: (visible: boolean) => void
  backUrl?: string
}> = observer(({ setVisible, backUrl }) => {
  const { handleSendDeviceInfo } = useDeviceInfo()
  const [form] = MyForm.useForm()
  const [isChecked, setIsChecked] = useState(false)
  const [loading, setLoading] = useState(false)
  const { store } = useStores()

  const [mobileType, setMobileType] = useState<AreaCodeEnum>(AreaCodeEnum.CN)

  const [getMobileCodeAction] = useMutation<
    getMobileCode,
    getMobileCodeVariables
  >(GET_MOBILE_CODE)

  const [loginByMobileAction] = useMutation<
    loginByMobile,
    loginByMobileVariables
  >(LOGIN_BY_MOBILE)

  const onMobileCode = useCallback(
    (validInfo: ValidInfoProps) => {
      const { mobile, areaCode } = form.getFieldsValue()

      getMobileCodeAction({
        variables: {
          mobile: mobile,
          behaviorParam: {
            token: validInfo.token,
            authenticate: validInfo.authenticate,
          },
          areaCode,
        },
      })
        .then(() => {
          message.destroy()
          message.success('获取验证码成功')
        })
        .catch(error => {
          message.destroy()
          message.error(graphQLErrorMessage(error) || '获取验证码失败')
        })
    },
    [form, getMobileCodeAction]
  )

  const beforeStart = useCallback(async (next: () => void) => {
    await form.validateFields(['mobile'])
    next()
  }, [])

  const onConfirm = useCallback(async () => {
    const formData = await form.validateFields()
    if (!isChecked) {
      message.destroy()
      message.error('请先勾选同意相关协议')
      return
    }

    setLoading(true)

    const inviteCodeQuery: { inviteCode?: string } = {}
    if (!!store.inviterCode) {
      inviteCodeQuery.inviteCode = store.inviterCode
    }

    loginByMobileAction({
      variables: {
        mobile: formData.mobile,
        code: formData.code,
        areaCode: formData.areaCode,
        ...inviteCodeQuery,
      },
    })
      .then(data => {
        Cookies.set('gate-token', data?.data?.data?.token || '', {
          expires: 365,
        })
        setVisible?.(false)
        crossLogin(data.data?.data?.tokenVoucher!, backUrl)
        handleSendDeviceInfo()
      })
      .catch(error => {
        message.destroy()
        message.error(graphQLErrorMessage(error) || '登录失败')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [form, isChecked])

  const regMobile = useMemo(() => {
    return handleMobileReg(mobileType)
  }, [mobileType])

  return (
    <MyForm $dark form={form}>
      <MyForm.Item
        name="mobile"
        rules={[
          {
            required: true,
            message: '请输入手机号',
          },
          {
            pattern: regMobile,
            message: '请输入正确的手机号',
          },
        ]}
      >
        <Input
          placeholder="手机号"
          addonBefore={
            <AreaCodeSelector onChange={val => setMobileType(val)} />
          }
        />
      </MyForm.Item>
      <MyForm.Item
        name="code"
        rules={[{ required: true, message: '请输入手机验证码' }]}
      >
        <Input
          placeholder="手机验证码"
          addonAfter={
            <Captcha onSuccess={onMobileCode} beforeStart={beforeStart} />
          }
        />
      </MyForm.Item>
      <div style={{ marginBottom: 18 }}>
        <MyCheckbox
          checked={isChecked}
          onChange={e => setIsChecked(e.target.checked)}
          $dark
        >
          我已阅读并同意《
          <MyLink
            href="/us/user-agreement"
            exist={true}
            className="primary-color"
          >
            用户协议
          </MyLink>
          》和《
          <MyLink href="/us/privacy" exist={true} className="primary-color">
            隐私政策
          </MyLink>
          》
        </MyCheckbox>
      </div>
      <MyBtn
        onClick={onConfirm}
        style={{ marginBottom: 16 }}
        type="primary"
        size="large"
        loading={loading}
        block
      >
        确定
      </MyBtn>
    </MyForm>
  )
})
